import { useEffect, useState } from "react";
import { Wallet, TrendingUp, Receipt, Package } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatCard } from "@/components/StatCard";
import { SalesChart } from "@/components/SalesChart";
import { api, formatRupiah, localDate, daysAgo, PAYMENT_LABEL } from "@/lib/api";

const monthStart = () => { const d = new Date(); d.setDate(1); return localDate(d); };
const PRESETS = [
  { id: "today", label: "Hari Ini", range: () => [localDate(), localDate()] },
  { id: "7d", label: "7 Hari", range: () => [daysAgo(6), localDate()] },
  { id: "month", label: "Bulan Ini", range: () => [monthStart(), localDate()] },
  { id: "30d", label: "30 Hari", range: () => [daysAgo(29), localDate()] },
];

export default function Laporan() {
  const [preset, setPreset] = useState("7d");
  const [[start, end], setRange] = useState(PRESETS[1].range());
  const [data, setData] = useState(null);

  useEffect(() => {
    if (start && end && start <= end) api.get("/reports", { params: { start, end } }).then((r) => setData(r.data));
  }, [start, end]);

  const pick = (p) => { setPreset(p.id); setRange(p.range()); };
  const custom = (i) => (e) => { setPreset("custom"); setRange((r) => (i === 0 ? [e.target.value, r[1]] : [r[0], e.target.value])); };
  const maxQty = Math.max(1, ...(data?.top_products || []).map((p) => p.qty));

  return (
    <div className="space-y-6" data-testid="laporan-page">
      <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900 md:text-4xl">Laporan Penjualan</h1>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap rounded-full bg-white p-1 ring-1 ring-slate-200">
          {PRESETS.map((p) => (
            <button key={p.id} onClick={() => pick(p)} data-testid={`laporan-preset-${p.id}`}
              className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${preset === p.id ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              {p.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Input type="date" value={start} onChange={custom(0)} className="h-10 w-auto bg-white" data-testid="laporan-start-date" />
          <span className="text-slate-400">&ndash;</span>
          <Input type="date" value={end} onChange={custom(1)} className="h-10 w-auto bg-white" data-testid="laporan-end-date" />
        </div>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <StatCard label="Pendapatan" value={formatRupiah(data.revenue)} icon={Wallet} testId="laporan-revenue" sub={<span data-testid="laporan-shipping">Termasuk ongkir {formatRupiah(data.shipping)}</span>} />
            <StatCard label="Estimasi Laba" value={formatRupiah(data.profit)} icon={TrendingUp} tone="sky" testId="laporan-profit" sub="Pendapatan - ongkir - harga modal" delay={60} />
            <StatCard label="Transaksi" value={data.count} icon={Receipt} tone="slate" testId="laporan-count" delay={120} />
            <StatCard label="Item Terjual" value={data.items_sold} icon={Package} tone="amber" testId="laporan-items-sold" delay={180} />
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 lg:col-span-2">
              <h3 className="mb-2 font-display text-lg font-semibold text-slate-800">Grafik Penjualan Harian</h3>
              <SalesChart data={data.series} testId="laporan-sales-chart" />
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <h3 className="mb-4 font-display text-lg font-semibold text-slate-800">Metode Pembayaran</h3>
              <div className="space-y-3">
                {Object.entries(data.payment_methods).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3" data-testid={`laporan-payment-${k}`}>
                    <span className="text-sm font-medium text-slate-600">{PAYMENT_LABEL[k]}</span>
                    <span className="font-mono text-sm font-bold text-slate-900">{formatRupiah(v)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white p-5">
            <h3 className="mb-3 font-display text-lg font-semibold text-slate-800">Produk Terlaris</h3>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">#</TableHead>
                  <TableHead>Produk</TableHead>
                  <TableHead>Terjual</TableHead>
                  <TableHead className="text-right">Pendapatan</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.top_products.map((p, i) => (
                  <TableRow key={p.product_id} data-testid={`laporan-top-product-${i}`}>
                    <TableCell className="font-mono text-slate-400">{i + 1}</TableCell>
                    <TableCell className="font-medium text-slate-800">{p.name}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="h-2 w-24 overflow-hidden rounded-full bg-slate-100">
                          <div className="h-full rounded-full bg-emerald-500" style={{ width: `${(p.qty / maxQty) * 100}%` }} />
                        </div>
                        <span className="font-mono text-sm">{p.qty}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-mono font-semibold text-emerald-700">{formatRupiah(p.revenue)}</TableCell>
                  </TableRow>
                ))}
                {data.top_products.length === 0 && (
                  <TableRow><TableCell colSpan={4} className="py-8 text-center text-slate-500" data-testid="laporan-empty">Belum ada penjualan pada periode ini.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
}
